/**
 * FeedbackNeededBanner (MED-330)
 * Purple callout banner on coach cohort sessions page when midpoint/endpoint
 * feedback is awaiting the coach's response.
 * Positioned below the AttendanceNeededBanner, above filter toggles.
 */

import { useState, useEffect } from 'react';
import { Alert, Text, Group, Button, Stack } from '@mantine/core';
import { FileText } from 'react-feather';
import coachPortalApi from '../../../services/api/coachPortal';
import { parseDateOnly } from '../../../utils/dateHelpers';
import classes from './FeedbackNeededBanner.module.css';

interface FeedbackNeededBannerProps {
  cohortId: string;
  onCompleteFeedback: (surveyType: 'midpoint' | 'endpoint') => void;
}

interface PendingSurvey {
  type: 'midpoint' | 'endpoint';
  isOverdue: boolean;
}

const HOURS_24_MS = 24 * 60 * 60 * 1000;

export function FeedbackNeededBanner({
  cohortId,
  onCompleteFeedback,
}: FeedbackNeededBannerProps) {
  const [pendingSurveys, setPendingSurveys] = useState<PendingSurvey[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkPendingFeedback = async () => {
      try {
        const pending: PendingSurvey[] = [];
        const now = Date.now();

        // Check both midpoint and endpoint
        for (const surveyType of ['midpoint', 'endpoint'] as const) {
          try {
            const data = await coachPortalApi.getCoachFeedback(cohortId, surveyType);
            if (data.config && data.config.schedule_status === 'sent' && !data.submission) {
              // MED-618: Compute overdue based on survey_deadline (24h+ past due)
              let isOverdue = false;
              const deadline = parseDateOnly(data.config.survey_deadline);
              if (deadline) {
                // MED-1265: measure from local midnight of the deadline's calendar
                // date — the raw UTC parse flagged overdue on the deadline evening.
                const elapsed = now - deadline.getTime();
                isOverdue = elapsed >= HOURS_24_MS;
              }
              pending.push({ type: surveyType, isOverdue });
            }
          } catch {
            // No config exists — skip
          }
        }

        setPendingSurveys(pending);
      } catch {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };

    checkPendingFeedback();
  }, [cohortId]);

  if (loading || pendingSurveys.length === 0) return null;

  return (
    <Stack gap="sm" mb="lg">
      {pendingSurveys.map((survey) => {
        const bannerColor = survey.isOverdue ? 'red' : 'violet';
        return (
          <Alert
            className={classes.banner}
            key={survey.type}
            color={bannerColor}
            variant="light"
            styles={{
              root: {
                borderLeft: `4px solid var(--mantine-color-${bannerColor}-6)`,
              },
            }}
          >
            <Group justify="space-between" wrap="nowrap" className={classes.row}>
              <Group gap="sm" wrap="nowrap">
                <FileText size={20} color={`var(--mantine-color-${bannerColor}-6)`} />
                <div>
                  <Text fw={600} size="sm">
                    {survey.type === 'midpoint'
                      ? survey.isOverdue ? 'Midpoint Feedback Overdue' : 'Midpoint Feedback Ready'
                      : survey.isOverdue ? 'Endpoint Feedback Overdue' : 'Endpoint Feedback Ready'}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {survey.type === 'midpoint'
                      ? 'Please share your coaching experience'
                      : 'Please share your overall program assessment'}
                  </Text>
                </div>
              </Group>

              <Button
                className={classes.action}
                size="xs"
                color={bannerColor}
                variant="filled"
                onClick={() => onCompleteFeedback(survey.type)}
              >
                Complete Feedback
              </Button>
            </Group>
          </Alert>
        );
      })}
    </Stack>
  );
}

export default FeedbackNeededBanner;
